import React, { useEffect, useState } from 'react';
import { useService } from '@Client/runner.hooks/useService';
import { SharedAngular } from '@Client/@types/sharedAngular';
import KendoReactDropDownTree from '@Shared.Angular/kendo.react/KendoReactDropDownTree';
import { CategoryDeleteWarningStatus } from '@Shared.Angular/flowingly.services/flowingly.constants';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { ISubCategoryFlowModel } from '@Shared.Angular/flowingly.services/category.api.service';
import { Guid } from '@Shared.Angular/@types/guid';

type Props = {
  deleteStatus: number;
  closeModal: () => void;
  categoryName: string;
  id: string;
  flowModelCount: number;
  procedureCount: number;
  onCategoryDeleted: () => void;
  subCategoriesToBeDeleted: ISubCategoryFlowModel[];
};

const WarningText = (
  deleteStatus: number,
  categoryName: string,
  deleteText: string,
  handleInputChange,
  flowModelCount: number,
  procedureCount: number
) => (
  <div>
    <fieldset
      className="categoriesv2-delete-modal-fieldset"
      style={{ padding: '15px 15px 30px' }}
    >
      <legend className="categoriesv2-delete-modal-legend">
        <b>Warning</b>
      </legend>
      <div>
        You are about to delete <b>{categoryName}</b>.
      </div>
      <div>This cannot be undone!</div>
      <br />
      <div>
        {(deleteStatus === CategoryDeleteWarningStatus.subCategoriesWithFlows ||
          deleteStatus === CategoryDeleteWarningStatus.reassignCategory) && (
          <div>
            <i
              className="fa-light fa-triangle-exclamation categoriesv2-delete-modal-warning-icon"
              aria-hidden="true"
            ></i>
            &nbsp;{flowModelCount} Flow Models will be reassigned to a new
            category.
          </div>
        )}
        {procedureCount > 0 && (
          <div>
            <i
              className="fa-light fa-triangle-exclamation categoriesv2-delete-modal-warning-icon"
              aria-hidden="true"
            ></i>
            &nbsp;{procedureCount} SOPs will be reassigned to a new category.
          </div>
        )}
      </div>
      <div>
        Type <b>DELETE</b> to continue
      </div>
      <input
        id="delete-modal"
        className="categoriesv2-delete-modal-textfield"
        type="text"
        value={deleteText}
        onChange={handleInputChange}
      />
    </fieldset>
  </div>
);

const CategoryDeleteModal = (props: Props) => {
  const {
    deleteStatus,
    closeModal,
    categoryName,
    id,
    flowModelCount,
    procedureCount,
    onCategoryDeleted,
    subCategoriesToBeDeleted
  } = props;

  const [backData, setBackData] = useState([]);
  const [deleteText, setDeleteText] = useState('');
  const [replacementCategory, setReplacementCategory] = useState(null);
  const [deleteButtonEnabled, setDeleteButtonEnabledStatus] = useState(false);

  const onCategorySelected = (
    _id: string,
    _index: string,
    _name: string,
    item: object
  ) => {
    setReplacementCategory(item);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeleteText(event.target.value);
  };

  useEffect(() => {
    setDeleteButtonStatus(deleteStatus);
  }, [replacementCategory, deleteText]);

  const setDeleteButtonStatus = (status: number) => {
    if (status === CategoryDeleteWarningStatus.subCategoriesWithFlows) {
      setDeleteButtonEnabledStatus(false);
    } else if (status === CategoryDeleteWarningStatus.reassignCategory) {
      setDeleteButtonEnabledStatus(
        deleteText.toLowerCase() === 'delete' && replacementCategory
      );
    } else {
      setDeleteButtonEnabledStatus(deleteText.toLowerCase() === 'delete');
    }
  };

  const categoryApiService =
    useService<SharedAngular.CategoryApiService>('categoryApiService');

  const notificationService = useService<SharedAngular.NotificationService>(
    'notificationService'
  );

  const appInsightsService =
    useService<SharedAngular.AppInsightsService>('appInsightsService');

  const deleteCategory = async () => {
    appInsightsService.startEventTimer('subcategoriesdeleted');
    if (id === replacementCategory?.id) {
      notificationService.showErrorToast(
        'Category to reassign to cannot be the one you are deleting'
      );
      setDeleteText('');
      return;
    }
    try {
      await categoryApiService.deleteCategory(
        id as Guid,
        replacementCategory?.id
      );
      closeModal();
      await onCategoryDeleted();
      notificationService.showSuccessToast('Category Deleted Successfully');
    } catch (err) {
      console.error(err);
      notificationService.showErrorToast('Error while deleting category');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await categoryApiService.getCategories();
        setBackData(response);
      } catch (error) {
        setBackData([]);
      }
    };
    fetchData();
  }, []);

  return (
    <Modal
      id="delete-category-modal"
      title="Delete Category"
      actionButtonText="Delete Category"
      actionButtonType="danger"
      isActionDisabled={!deleteButtonEnabled}
      onCancelClick={closeModal}
      onClose={closeModal}
      onActionClick={deleteCategory}
    >
      {deleteStatus === CategoryDeleteWarningStatus.canBeDeleted ? (
        <div>
          {WarningText(
            deleteStatus,
            categoryName,
            deleteText,
            handleInputChange,
            flowModelCount,
            procedureCount
          )}
        </div>
      ) : deleteStatus === CategoryDeleteWarningStatus.reassignCategory ? (
        <div className="categoriesv2-delete-modal-outer-content-container">
          <div className="categoriesv2-delete-modal-content-container">
            <div className="categoriesv2-delete-modal-inner-content-container">
              <div className="categoriesv2-delete-modal-inner-content-container-text">
                <b>Reassign Category</b>
              </div>
              <br />
              <div className="categoriesv2-delete-modal-inner-content-container-text">
                To ensure in progress & published flows continue to work, search
                for a category to replace <b>{categoryName}</b>
              </div>
            </div>
            <div className="categoriesv2-delete-modal-inner-content-container">
              <KendoReactDropDownTree
                id="delete-category-modal-dropdown-tree"
                categories={backData}
                categoryClicked={onCategorySelected}
                dataItemKey="id"
                textField="name"
                subItemsField="subCategories"
                placeholder="Select Category"
                showFilter={true}
                className="delete-category-dropdown-tree-popup"
              />
            </div>
          </div>
          <br />
          <div className="categoriesv2-delete-modal-inner-content-container-text">
            When you delete a parent category, all of its sub-categories will be
            deleted as well.
          </div>
          <br />
          <div>
            {WarningText(
              deleteStatus,
              categoryName,
              deleteText,
              handleInputChange,
              flowModelCount,
              procedureCount
            )}
          </div>
        </div>
      ) : (
        <div className="categoriesv2-delete-modal-last-content-container">
          <div className="categoriesv2-delete-modal-inner-content-container-text">
            <b> {categoryName}</b> contains sub-categories with flow models
            published to them.
            <br />
            <br />
            Before deleting, you must first reassign the flow models that are
            published in these sub-categories to another category.
          </div>
          <br />
          <table id="subcategory-published-flow-models-table">
            <thead>
              <tr>
                <th>Sub-Category</th>
                <th>Flow Models Published</th>
              </tr>
            </thead>
            <tbody>
              {subCategoriesToBeDeleted.map((subCategory) => (
                <tr
                  key={subCategory.subCategoryName}
                  className="v2-categories-row"
                >
                  <td scope="row">{subCategory.subCategoryName}</td>
                  <td>{subCategory.flowModelCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

export default CategoryDeleteModal;
